import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { ArrowUp, Check, FileText, Pause, Play, RotateCcw } from "lucide-react";
import { useUserContext } from "../Context/UserContext";
import { PortalRoute } from "../Portal/PortalContext";
import SignUpPage from "../Login/SignUp";
import { US_STATES } from "../Questions/intake";
import { useReviewPrice, formatPrice } from "../Reviews/reviewPrice";
import {
  discardGuestDraft,
  readGuestDraft,
  saveGuestDraft,
} from "./guestDraft";
import "./public.css";

const waits = [1800, 2200, 1800, 3600, 2200, 3300, 4000, 3000];
export function LandingPage() {
  const [saved] = useState(readGuestDraft);
  const [country, setCountry] = useState(saved?.country || "US"),
    [region, setRegion] = useState(saved?.region ?? "California"),
    [topic, setTopic] = useState(saved?.topic || "contracts");
  const [text, setText] = useState(saved?.text || ""),
    [register, setRegister] = useState(false),
    [stored, setStored] = useState(true);
  const [step, setStep] = useState(0),
    [playing, setPlaying] = useState(
      () => !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    );
  const price = useReviewPrice();
  const law =
    topic === "contracts"
      ? "contract law"
      : topic === "employment"
        ? "employment law"
        : topic === "business"
          ? "business law"
          : "your legal question";
  const context =
    country === "US"
      ? region || "the United States"
      : country === "CA"
        ? "Canada"
        : "the United Kingdom";
  useEffect(() => {
    if (!playing || step >= 8) return;
    const t = setTimeout(() => setStep(s => s + 1), waits[step]);
    return () => clearTimeout(t);
  }, [playing, step]);
  function requestAccess(value = text) {
    setPlaying(false);
    setStored(saveGuestDraft({ text: value, country, region, topic }));
    setRegister(true);
  }
  return (
    <div className="public-home">
      <a className="public-skip" href="#public-chat">
        Skip to chat
      </a>
      <header className="public-nav">
        <Link className="public-brand" to="/">
          Reviewed<span>.</span>
        </Link>
        <nav aria-label="Main navigation">
          <a href="#how-it-works">How it works</a>
          <a href="#review-fee">Review & pricing</a>
          <Link to="/login">Sign in</Link>
        </nav>
      </header>
      <main>
        <section className="public-hero">
          <p className="public-kicker">EARLY ACCESS · TEST BETA</p>
          <h1>
            Get your AI answers
            <br />
            <em>reviewed by an attorney.</em>
          </h1>
          <p>
            See the fixed fee before requesting review.
            {price
              ? ` $${price.priceCents / 100} USD per review.`
              : " Pricing is temporarily unavailable."}
          </p>
          <small>
            This beta uses example materials. No payment is collected. Attorney
            review is limited to California contract questions.
          </small>
        </section>
        <section
          className="public-chat"
          id="public-chat"
          aria-label="Legal question chat"
        >
          <div className="public-settings">
            <label>
              Country:{" "}
              <select
                aria-label="Country"
                value={country}
                onChange={e => {
                  setCountry(e.target.value);
                  setRegion("");
                  setStep(0);
                  setPlaying(false);
                }}
              >
                <option value="US">US</option>
                <option value="CA">Canada</option>
                <option value="GB">UK</option>
              </select>
            </label>
            {country === "US" && (
              <label>
                State:{" "}
                <select
                  aria-label="State"
                  value={region}
                  onChange={e => {
                    setRegion(e.target.value);
                    setStep(0);
                    setPlaying(false);
                  }}
                >
                  <option value="">Not sure</option>
                  {US_STATES.map(s => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Area of law:{" "}
              <select
                aria-label="Area of law"
                value={topic}
                onChange={e => {
                  setTopic(e.target.value);
                  setStep(0);
                  setPlaying(false);
                }}
              >
                <option value="contracts">Contract</option>
                <option value="employment">Employment</option>
                <option value="business">Business</option>
                <option value="other">Not sure</option>
              </select>
            </label>
          </div>
          <div className="public-transcript">
            <div className="public-demo-controls">
              <span>Illustrative conversation · nothing is sent</span>
              <div>
                <button
                  aria-label={playing ? "Pause example" : "Play example"}
                  onClick={() => {
                    if (step >= 8) setStep(0);
                    setPlaying(!playing);
                  }}
                >
                  {playing ? <Pause size={15} /> : <Play size={15} />}
                </button>
                <button
                  aria-label="Restart example"
                  onClick={() => {
                    setStep(0);
                    setPlaying(true);
                  }}
                >
                  <RotateCcw size={15} />
                </button>
              </div>
            </div>
            <article className="public-message">
              <span className="public-avatar">R</span>
              <div>
                <strong>Reviewed AI</strong>
                <p>
                  I’m an AI focused on {context} {law}. I’ll do my best to
                  explain it, but my answers aren’t legal advice. You can
                  request an attorney review of an answer.
                </p>
              </div>
            </article>
            {step >= 1 && (
              <article className="public-message public-user">
                <div>
                  <strong>You · example</strong>
                  <div className="public-file">
                    <FileText size={20} />
                    <span>
                      Consulting agreement.pdf
                      <small>Example file · not uploaded</small>
                    </span>
                  </div>
                  <p>
                    This agreement says either party can terminate immediately.
                    Where should I check for notice and final payment?
                  </p>
                </div>
              </article>
            )}
            {step === 2 && (
              <p className="public-working" role="status">
                Checking the example’s notice and payment terms…
              </p>
            )}
            {step >= 3 && (
              <article className="public-message">
                <span className="public-avatar">R</span>
                <div>
                  <strong>Reviewed AI · example</strong>
                  <p>
                    Start with the termination section, then check any
                    cross-references to notice, fees, and payment. The phrase
                    alone does not tell us every obligation in the agreement.
                  </p>
                </div>
              </article>
            )}
            {step >= 4 && (
              <article className="public-message public-user">
                <div>
                  <strong>You · example</strong>
                  <p>Can you also check whether unpaid invoices are covered?</p>
                </div>
              </article>
            )}
            {step === 5 && (
              <p className="public-working" role="status">
                Checking the example’s fee and invoice sections…
              </p>
            )}
            {step >= 6 && (
              <article className="public-message">
                <span className="public-avatar">R</span>
                <div>
                  <strong>Reviewed AI · example</strong>
                  <p>
                    I would compare the invoice terms with the termination
                    clause and flag any uncertainty. An attorney can assess the
                    full submitted record.
                  </p>
                  <fieldset className="public-review-choice">
                    <legend>
                      Attorney review?{" "}
                      <span>
                        {price
                          ? `${formatPrice(price.priceCents)} fixed fee`
                          : "Fee unavailable"}
                      </span>
                    </legend>
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setStep(7);
                          setPlaying(false);
                        }}
                      >
                        Yes
                      </button>
                      <input
                        aria-label="Example review instruction"
                        value="Please verify this answer."
                        readOnly
                      />
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => {
                          setStep(6);
                          setPlaying(false);
                        }}
                      >
                        Not now
                      </button>
                    </div>
                    <small>
                      Example only. Request access below to start your own
                      conversation.
                    </small>
                  </fieldset>
                </div>
              </article>
            )}
            {step >= 7 && (
              <div className="public-example-receipt">
                <Check size={17} />
                <div>
                  <strong>Example review request</strong>
                  <p>
                    Scope and fee shown before submission. Matching and
                    acceptance come first. No charge is made in this beta.
                  </p>
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => {
                      setStep(8);
                      setPlaying(false);
                    }}
                  >
                    See example attorney response
                  </button>
                </div>
              </div>
            )}
            {step >= 8 && (
              <article className="public-message public-attorney">
                <span className="public-avatar">A</span>
                <div>
                  <strong>Attorney · fictional example</strong>
                  <p>
                    I would check the submitted termination and payment terms
                    together, identify any inconsistent language, and explain
                    what the written assessment covers. Rewriting the agreement
                    would need a separate scope.
                  </p>
                  <small>
                    Illustration of a separate attorney role; this is not a live
                    review or legal advice.
                  </small>
                </div>
              </article>
            )}
          </div>
          <form
            className="public-composer"
            onSubmit={e => {
              e.preventDefault();
              requestAccess();
            }}
          >
            <textarea
              aria-label="Ask a question"
              placeholder="Ask a question…"
              value={text}
              maxLength={20000}
              rows={2}
              onChange={e => {
                setText(e.target.value);
                if (!register) requestAccess(e.target.value);
              }}
            />
            <div>
              <Link to="/privilege">About attorney-client privilege*</Link>
              <button type="submit" aria-label="Request access to chat">
                <ArrowUp size={20} />
              </button>
            </div>
          </form>
        </section>
        <section className="public-how" id="how-it-works">
          <h2>
            Start a conversation.
            <br />
            Know what comes next.
          </h2>
          <div className="public-how-grid">
            <article>
              <span>01</span>
              <h3>Ask with AI</h3>
              <p>
                Choose a location and topic. Use generic facts while testing.
                Accounts are approved individually.
              </p>
            </article>
            <article>
              <span>02</span>
              <h3>Agree on the review</h3>
              <p>
                See the scope, fixed fee, and turnaround. We check coverage and
                match an approved attorney, who can accept or decline.
              </p>
            </article>
            <article>
              <span>03</span>
              <h3>Read their assessment</h3>
              <p>
                The attorney’s answer is separate from the AI chat. Your
                submitted record, answer, and included clarification stay
                together.
              </p>
            </article>
          </div>
        </section>
        <section className="public-fee" id="review-fee">
          <div>
            <p className="public-kicker">WHAT THE TEST REVIEW INCLUDES</p>
            <h2>
              One question.
              <br />
              One written assessment.
            </h2>
            <p>
              Corrections, limitations, and recommended next steps based on your
              submitted conversation and documents. Contract rewriting, new
              matters, and ongoing representation need separate work.
            </p>
          </div>
          <div className="public-fee-card">
            <strong>
              {price
                ? `${formatPrice(price.priceCents)} USD`
                : "Fee unavailable"}
            </strong>
            <p>Fixed fee per review</p>
            <p>
              Attorney review in this beta: California contract questions only,
              subject to participating reviewer availability. Use generic or
              made-up facts.
            </p>
            <ul>
              <li>
                {price ? price.turnaroundHours : "—"} calendar hours after the
                agreed review starts
              </li>
              <li>One clarification round within 7 days of delivery</li>
              <li>Matching happens first; availability is not guaranteed</li>
            </ul>
            <small>Beta test — no charge will be made.</small>
            <button className="btn btn-primary" onClick={() => requestAccess()}>
              Request access
            </button>
          </div>
        </section>
        <section className="public-confidentiality">
          <h2>Be careful with sensitive information.</h2>
          <p>
            The AI is not your attorney. This test beta does not establish an
            attorney-client relationship. Use a generic example before sharing
            confidential facts. We don’t promise blanket privilege or zero data
            retention.
          </p>
          <Link to="/privilege">Read our explanation →</Link>
        </section>
      </main>
      <footer className="public-footer">
        <Link className="public-brand" to="/">
          Reviewed<span>.</span>
        </Link>
        <nav aria-label="Footer">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/beta">Beta scope</Link>
          <Link to="/work-with-us">For attorneys</Link>
          <a href="mailto:support@reviewed.work">Contact</a>
        </nav>
        <small>
          Approval-only test beta. No real legal matter or deadline should
          depend on this service.
        </small>
      </footer>
      <Modal
        show={register}
        onHide={() => setRegister(false)}
        centered
        scrollable
        aria-labelledby="public-registration-title"
        className="public-registration"
      >
        <Modal.Header closeButton>
          <Modal.Title id="public-registration-title">
            Continue with Reviewed
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!stored && (
            <p role="alert">
              This browser couldn’t save your draft. Keep a copy before leaving.
            </p>
          )}
          <label className="w-100">
            Your unsent question
            <textarea
              className="form-control my-2"
              aria-label="Unsent question draft"
              autoFocus
              onFocus={e =>
                e.currentTarget.setSelectionRange(
                  e.currentTarget.value.length,
                  e.currentTarget.value.length
                )
              }
              rows={3}
              maxLength={20000}
              value={text}
              onChange={e => {
                setText(e.target.value);
                setStored(
                  saveGuestDraft({
                    text: e.target.value,
                    country,
                    region,
                    topic,
                  })
                );
              }}
            />
          </label>
          <SignUpPage embedded returnPath="/questions/new?resume=1" />
          {text && (
            <button
              className="btn btn-link"
              onClick={() => {
                discardGuestDraft();
                setText("");
                setRegister(false);
              }}
            >
              Discard this chat draft
            </button>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
export default function PublicHome() {
  const { user, isAuthLoading, authError, refetchUser } = useUserContext();
  if (isAuthLoading)
    return (
      <main className="public-notice" role="status">
        Opening Reviewed…
      </main>
    );
  if (user) return <PortalRoute home />;
  return (
    <>
      {authError && (
        <div className="alert alert-warning" role="alert">
          We couldn’t verify your session.{" "}
          <button className="btn btn-link" onClick={refetchUser}>
            Retry
          </button>
        </div>
      )}
      <LandingPage />
    </>
  );
}
